import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import { TTab } from '@src/types';
import { cn } from '@lib/utils.ts';

interface TabProps {
  tabActive: number;
  tabs: TTab[];
  onChangeTab: (value: number) => void;
  className?: string;
  size?: 'lg' | 'sm' | 'xs' | 'xl' | '2xl' | 'custom';
  variant?: 'default' | 'gradient';
  fontWeight?: number;
  /**
   * Keep all tab contents mounted and only toggle visibility.
   * Useful for preserving form state when switching tabs.
   */
  keepMounted?: boolean;
}

export const Tab: FC<TabProps> = ({
  tabActive,
  tabs,
  onChangeTab,
  className,
  size,
  variant,
  fontWeight,
  keepMounted,
}) => {
  const [boxStyle, setBoxStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const activeTab = tabRefs.current[tabActive];

      if (activeTab) {
        setBoxStyle({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth,
        });
      }
    });

    tabRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, [tabActive, tabs]);

  useEffect(() => {
    const activeTab = tabRefs.current[tabActive];

    if (activeTab) {
      setBoxStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, [tabActive]);

  const stepActive = useMemo(() => tabs?.[tabActive], [tabs, tabActive]);

  const textSize = useMemo(() => {
    switch (size) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'xl':
        return 'text-xl';
      case '2xl':
        return 'text-2xl';
      case 'custom':
        return 'text-[23px]';
      default:
        return 'text-lg';
    }
  }, [size]);

  const pb = useMemo(() => {
    if (variant === 'gradient') return 'pb-2';
    switch (size) {
      case 'xs':
        return 'pb-1.5';
      case 'sm':
        return 'pb-2.5';
      case 'xl':
        return 'pb-2';
      case '2xl':
        return 'pb-2';
      default:
        return 'pb-3.5';
    }
  }, [size, variant]);

  const height = useMemo(() => {
    return variant === 'gradient' ? 44 : 2;
  }, [variant]);

  return (
    <Fragment>
      {stepActive?.header && stepActive.header}
      <div className={cn('flex relative gap-x-7 mb-5', pb, className)}>
        <div
          className={cn(
            'absolute rounded-[2px] bottom-0',
            variant === 'gradient'
              ? 'bg-gradient-to-r from-tertiary to-primary rounded-[10px]'
              : 'bg-blue'
          )}
          style={{
            width: boxStyle.width,
            left: boxStyle.left,
            zIndex: 0,
            height: height,
            transition: 'all 0.3s',
          }}
        />
        {tabs.map((e, i) => (
          <div
            key={`${e.label}_index${i}`}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            className={cn(
              `z-1 ${e.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative flex items-center`,
              variant === 'gradient' ? 'px-8' : ''
            )}
            style={{ transition: 'all 0.3s' }}
            onClick={() => {
              if (!e.disabled) {
                onChangeTab(i);
              }
            }}
          >
            <p
              className={cn(textSize)}
              style={{
                fontWeight: fontWeight
                  ? fontWeight
                  : tabActive === i
                    ? 500
                    : 400,
                color:
                  tabActive !== i || variant === 'gradient'
                    ? 'white'
                    : '#4DA2FF',
              }}
            >
              {e.label}
            </p>
            {!isNil(e.count) ? (
              <div
                className={
                  'text-xs font-bold bg-[#C13839] h-5 flex items-center px-1 min-w-5 rounded-[3px] justify-center ml-1'
                }
              >
                {e.count}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {keepMounted ? (
        <div>
          {tabs.map((tab, idx) => (
            <div
              key={`${tab.label}_panel_${idx}`}
              className={idx === tabActive ? 'block' : 'hidden'}
            >
              {tab.component}
            </div>
          ))}
        </div>
      ) : (
        stepActive.component
      )}
    </Fragment>
  );
};
